import React, { useRef, useEffect, useCallback } from 'react';

/**
 * Custom 3-segment time input: [HH] : [MM] [AM/PM]
 * Tab cycles through segments internally, then exits to next field.
 * Value format: "HH:MM" (24-hour) to match existing data model.
 * 
 * Uses uncontrolled inputs during editing to avoid React cursor reset issues.
 */
const TimeInput = React.forwardRef(({
  value,
  onChange,
  onClick,
  onFocus,
  onKeyDown,
  className,
  'data-testid': dataTestId,
}, ref) => {
  const hourRef = useRef(null);
  const minRef = useRef(null);
  const ampmRef = useRef(null);
  const ampmValueRef = useRef('AM');
  const editingRef = useRef(false);

  const parse24h = useCallback((val) => {
    if (!val) return { hour: '', min: '', ampm: 'AM' };
    const [h, m] = val.split(':');
    const hi = parseInt(h, 10);
    if (isNaN(hi)) return { hour: '', min: '', ampm: 'AM' };
    const ampm = hi >= 12 ? 'PM' : 'AM';
    const h12 = hi === 0 ? 12 : hi > 12 ? hi - 12 : hi;
    return { hour: String(h12), min: m || '00', ampm };
  }, []);

  // Sync from prop when NOT editing
  useEffect(() => {
    if (!editingRef.current) {
      const p = parse24h(value);
      if (hourRef.current) hourRef.current.value = p.hour;
      if (minRef.current) minRef.current.value = p.min;
      ampmValueRef.current = p.ampm;
    }
  }, [value, parse24h]);

  // Initial render
  useEffect(() => {
    const p = parse24h(value);
    if (hourRef.current) hourRef.current.value = p.hour;
    if (minRef.current) minRef.current.value = p.min;
    ampmValueRef.current = p.ampm;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const to24h = (hour, min, ampm) => {
    let h = parseInt(hour, 10);
    if (isNaN(h)) return '';
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    const hStr = String(h).padStart(2, '0');
    const mStr = (min || '00').padStart(2, '0');
    return `${hStr}:${mStr}`;
  };

  const emitChange = () => {
    const hour = hourRef.current?.value || '';
    const min = minRef.current?.value || '';
    const ampm = ampmValueRef.current;
    if (hour) {
      const val24 = to24h(hour, min, ampm);
      if (val24) onChange(val24);
    }
  };

  const [, forceRender] = React.useState(0);

  React.useImperativeHandle(ref, () => hourRef.current);

  const markEditing = () => { editingRef.current = true; };
  const markDoneEditing = () => { editingRef.current = false; };

  const handleHourKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      minRef.current?.focus();
      minRef.current?.select();
      return;
    }
    if (e.key === 'Tab' && e.shiftKey) {
      if (onKeyDown) onKeyDown(e);
      return;
    }
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape', 'Enter'].includes(e.key)) {
      if (onKeyDown) onKeyDown(e);
      return;
    }
  };

  const handleMinKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      ampmRef.current?.focus();
      return;
    }
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      hourRef.current?.focus();
      hourRef.current?.select();
      return;
    }
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape', 'Enter'].includes(e.key)) {
      if (onKeyDown) onKeyDown(e);
      return;
    }
  };

  const handleAmpmKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      if (onKeyDown) onKeyDown(e);
      return;
    }
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      minRef.current?.focus();
      minRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      ampmValueRef.current = ampmValueRef.current === 'AM' ? 'PM' : 'AM';
      forceRender(n => n + 1);
      emitChange();
      return;
    }
    if (e.key === 'a' || e.key === 'A') {
      e.preventDefault();
      ampmValueRef.current = 'AM';
      forceRender(n => n + 1);
      emitChange();
      return;
    }
    if (e.key === 'p' || e.key === 'P') {
      e.preventDefault();
      ampmValueRef.current = 'PM';
      forceRender(n => n + 1);
      emitChange();
      return;
    }
    if (['ArrowLeft', 'ArrowRight', 'Escape', 'Enter'].includes(e.key)) {
      if (onKeyDown) onKeyDown(e);
      return;
    }
  };

  const handleHourInput = (e) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length > 2) v = v.slice(-2);
    const num = parseInt(v, 10);
    if (v && (num < 1 || num > 12)) {
      e.target.value = hourRef.current?._lastValid || '';
      return;
    }
    e.target.value = v;
    hourRef.current._lastValid = v;
    if (v.length === 2) {
      emitChange();
      minRef.current?.focus();
      minRef.current?.select();
    }
  };

  const handleMinInput = (e) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length > 2) v = v.slice(-2);
    const num = parseInt(v, 10);
    if (v && num > 59) {
      e.target.value = minRef.current?._lastValid || '';
      return;
    }
    e.target.value = v;
    minRef.current._lastValid = v;
    if (v.length === 2) {
      emitChange();
      ampmRef.current?.focus();
    }
  };

  const handleHourBlur = () => {
    markDoneEditing();
    emitChange();
  };

  const handleMinBlur = () => {
    markDoneEditing();
    const v = minRef.current?.value || '';
    if (v.length === 1) {
      minRef.current.value = v.padStart(2, '0');
    }
    emitChange();
  };

  const handleAmpmBlur = () => {
    markDoneEditing();
  };

  const handleContainerClick = (e) => {
    if (e.target === e.currentTarget) {
      if (onClick) onClick();
      hourRef.current?.focus();
      hourRef.current?.select();
    }
  };

  const handleSegmentFocus = () => {
    markEditing();
    if (onFocus) onFocus();
  };

  const inputBase = "bg-transparent outline-none text-center text-xs";

  return (
    <div
      className={className}
      onClick={handleContainerClick}
      style={{ display: 'flex', alignItems: 'center', cursor: 'text' }}
      data-testid={dataTestId}
    >
      <input
        ref={hourRef}
        type="text"
        inputMode="numeric"
        defaultValue=""
        onInput={handleHourInput}
        onBlur={handleHourBlur}
        onClick={() => { if (onClick) onClick(); }}
        onFocus={(e) => { handleSegmentFocus(); e.target.select(); }}
        onKeyDown={handleHourKeyDown}
        placeholder="--"
        maxLength={2}
        className={`${inputBase} w-[22px]`}
        data-testid={dataTestId ? `${dataTestId}-hour` : undefined}
      />
      <span className="text-xs text-muted-foreground">:</span>
      <input
        ref={minRef}
        type="text"
        inputMode="numeric"
        defaultValue=""
        onInput={handleMinInput}
        onBlur={handleMinBlur}
        onClick={() => { if (onClick) onClick(); }}
        onFocus={(e) => { handleSegmentFocus(); e.target.select(); }}
        onKeyDown={handleMinKeyDown}
        placeholder="--"
        maxLength={2}
        className={`${inputBase} w-[22px]`}
        data-testid={dataTestId ? `${dataTestId}-min` : undefined}
      />
      <button
        ref={ampmRef}
        type="button"
        tabIndex={0}
        onFocus={handleSegmentFocus}
        onBlur={handleAmpmBlur}
        onKeyDown={handleAmpmKeyDown}
        onClick={(e) => {
          e.stopPropagation();
          ampmValueRef.current = ampmValueRef.current === 'AM' ? 'PM' : 'AM';
          forceRender(n => n + 1);
          emitChange();
        }}
        className="text-xs ml-1 px-0.5 rounded hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
        data-testid={dataTestId ? `${dataTestId}-ampm` : undefined}
      >
        {ampmValueRef.current}
      </button>
    </div>
  );
});

TimeInput.displayName = 'TimeInput';

export default TimeInput;
